import type { ReleaseTest } from '../data/release-tests-data';
import type { TestResultData } from '../hooks/useTestResults';

function escapeCSV(value: string): string {
  if (!value) return '""';
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Generate CSV from filtered test data + results.
 * Includes UTF-8 BOM for Excel Hungarian character support.
 */
export function generateFilteredCsv(
  filteredTests: ReleaseTest[],
  results: TestResultData[]
): string {
  const headers = [
    'Version',
    'Feature Index',
    'Feature Name (EN)',
    'Feature Name (HU)',
    'Status',
    'Tester',
    'Comment',
    'Steps Completed',
    'Expected (EN)',
    'Expected (HU)',
    'Tested At',
  ];

  const rows: string[] = [];

  for (const rt of filteredTests) {
    for (let fIdx = 0; fIdx < rt.features.length; fIdx++) {
      const feature = rt.features[fIdx]!;
      const result = results.find(r => r.version === rt.version && r.featureIndex === fIdx);

      let stepsDisplay = '';
      if (result?.stepResults) {
        try {
          const parsed = JSON.parse(result.stepResults) as Record<string, boolean>;
          const entries = Object.entries(parsed);
          const completed = entries.filter(([, v]) => v === true).length;
          stepsDisplay = `${completed}/${entries.length}`;
        } catch {
          stepsDisplay = '';
        }
      }

      rows.push(
        [
          rt.version,
          fIdx.toString(),
          escapeCSV(feature.nameEn),
          escapeCSV(feature.nameHu),
          result?.status ?? 'not_tested',
          escapeCSV(result?.testerName ?? ''),
          escapeCSV(result?.comment ?? ''),
          stepsDisplay,
          escapeCSV(feature.expected.en),
          escapeCSV(feature.expected.hu),
          result?.updatedAt ?? '',
        ].join(',')
      );
    }
  }

  const bom = '\uFEFF';
  return bom + [headers.join(','), ...rows].join('\n');
}

/**
 * Trigger browser download of CSV content.
 */
export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
